import {
	type AgentPermissionAgentRef,
	type AgentPermissionEntityType,
	type AgentPermissionPolicy,
	type AgentPermissionsResponse,
	EFFECT_STRICTNESS,
	type PermissionAction,
	type PermissionEffect,
	type PermissionResourceClass,
} from "@/lib/api/agent-permissions";

/**
 * Deterministic model helpers for the Permissions matrix. Pure functions only —
 * no React, no fetches — so the floor/autonomous-cap guard logic is unit
 * testable and shared between the matrix component and any future callers.
 */

/** Connector rules are edited in organization Policies and evaluated by the server. */
export type MatrixResourceClass = Exclude<
	PermissionResourceClass,
	"connector_action"
>;

/** The actions each matrix resource class exposes, in display order. */
export const CLASS_ACTIONS: Record<
	MatrixResourceClass,
	readonly PermissionAction[]
> = {
	entity: ["read", "create", "update", "delete"],
	// read = list/get peer agents; create/update/delete = definition CRUD.
	agent_config: ["read", "create", "update", "delete"],
	entity_schema: [
		"create_type",
		"update_type",
		"delete_type",
		"create_relationship_type",
		"update_relationship_type",
		"delete_relationship_type",
	],
};

/**
 * Server-side class defaults — the effect assumed when neither a floor row nor
 * an agent row names an action. Mirrors the backend's resolver defaults.
 */
export const CLASS_DEFAULTS: Record<
	MatrixResourceClass,
	Partial<Record<PermissionAction, PermissionEffect>>
> = {
	entity: { read: "auto", create: "auto", update: "auto", delete: "approval" },
	agent_config: {
		read: "auto",
		create: "approval",
		update: "approval",
		delete: "deny",
	},
	entity_schema: {
		create_type: "auto",
		update_type: "auto",
		delete_type: "auto",
		create_relationship_type: "auto",
		update_relationship_type: "auto",
		delete_relationship_type: "auto",
	},
};

/**
 * Effect choices for one action. Entity/agent_config `read` is auto/deny only
 * (approval is not a useful read outcome — the server collapses it to deny).
 */
export function effectOptionsForAction(
	resourceClass: MatrixResourceClass,
	action: PermissionAction,
): readonly PermissionEffect[] {
	if (
		(resourceClass === "entity" || resourceClass === "agent_config") &&
		action === "read"
	) {
		return ["auto", "deny"];
	}
	return ["auto", "approval", "deny"];
}

export const EFFECT_LABEL: Record<PermissionEffect, string> = {
	auto: "Auto",
	approval: "Ask",
	deny: "Block",
};

/**
 * The effect a policy row EXPLICITLY stores for one action — read from the sparse
 * `effects` map ONLY. It must return `undefined` for an action the row doesn't
 * name, so the folds abstain on it (inherit) rather than tighten. We deliberately
 * do NOT fall back to the flat `*_mode` columns: the server synthesizes those from
 * class defaults for omitted actions, so a fallback would make an abstaining row
 * participate in the fold (e.g. an agent row of only `create=approval` would read
 * `delete=approval` and mask an org `delete=auto`).
 */
function policyActionEffect(
	policy: AgentPermissionPolicy,
	action: PermissionAction,
): PermissionEffect | undefined {
	return policy.effects?.[action];
}

/** True iff `candidate` is looser (less strict) than `bound`. */
function isLooserThan(
	candidate: PermissionEffect,
	bound: PermissionEffect,
): boolean {
	return EFFECT_STRICTNESS[candidate] < EFFECT_STRICTNESS[bound];
}

/** The stricter of two effects, matching the server's policy fold. */
export function stricterEffect(
	a: PermissionEffect,
	b: PermissionEffect,
): PermissionEffect {
	return EFFECT_STRICTNESS[a] >= EFFECT_STRICTNESS[b] ? a : b;
}

/**
 * Resolve the org floor effect for one (class, action, entity_type).
 * The floor is the MOST-RESTRICTIVE effect across the matching floor rows that
 * NAME the action. The class default is a STARTING POINT, not a floor: if any
 * floor row names the action, the default drops out (an org row may set the floor
 * LOOSER than the default). Only when no floor row names the action does the
 * class default apply. This mirrors the backend resolver's fold exactly.
 *
 * Entity floor rows may be type-scoped: a row with `entity_type_slug` matches
 * only that type (plus the blanket rows with a null slug); a blanket floor row
 * (null slug) applies to every type.
 */
export function resolveFloor(
	floor: AgentPermissionPolicy[],
	resourceClass: MatrixResourceClass,
	action: PermissionAction,
	entityTypeSlug: string | null,
	targetAgentId: string | null = null,
): PermissionEffect {
	let folded: PermissionEffect | null = null;
	for (const row of floor) {
		if (row.resource_class !== resourceClass) continue;
		// Finer-than-type scopes do NOT bind the type-level matrix.
		if (row.field_path !== null || row.entity_id !== null) continue;
		if (
			row.entity_type_slug !== null &&
			row.entity_type_slug !== entityTypeSlug
		) {
			continue;
		}
		if (row.operation_key !== null) {
			continue;
		}
		if (row.target_agent_id !== null && row.target_agent_id !== targetAgentId) {
			continue;
		}
		const effect = policyActionEffect(row, action);
		if (effect)
			folded = folded === null ? effect : stricterEffect(folded, effect);
	}
	return folded ?? CLASS_DEFAULTS[resourceClass]?.[action] ?? "deny";
}

/**
 * Whether a real org-FLOOR row names this action (vs the effect being the mere
 * class-default fallback). Mirrors {@link resolveFloor}'s scope filter exactly.
 */
export function floorRowNamesAction(
	floor: AgentPermissionPolicy[],
	resourceClass: MatrixResourceClass,
	action: PermissionAction,
	entityTypeSlug: string | null,
	targetAgentId: string | null = null,
): boolean {
	return floor.some((row) => {
		if (row.resource_class !== resourceClass) return false;
		if (row.field_path !== null || row.entity_id !== null) return false;
		if (
			row.entity_type_slug !== null &&
			row.entity_type_slug !== entityTypeSlug
		) {
			return false;
		}
		if (row.operation_key !== null) {
			return false;
		}
		if (row.target_agent_id !== null && row.target_agent_id !== targetAgentId) {
			return false;
		}
		return policyActionEffect(row, action) !== undefined;
	});
}

/**
 * Whether a real BLANKET (null entity_type) agent row NAMES this action for the
 * given mode — vs the blanket resolver merely returning a class-default fallback.
 * Only a real blanket row is a lower bound on a per-type control: the backend folds
 * blanket + type MOST-RESTRICTIVE, so a stored blanket effect caps how loose the
 * type override may go. But when NO blanket row names the action, the blanket
 * resolves to the class default, which is NOT a bound — a sole per-type row legally
 * resolves to its own value (backend drops the class default the moment any row
 * names the action). Mirrors {@link findAgentRow} + {@link policyActionEffect}.
 */
export function blanketRowNamesAction(
	agent: AgentPermissionPolicy[],
	resourceClass: MatrixResourceClass,
	action: PermissionAction,
): boolean {
	const row = findAgentRow(agent, resourceClass, null);
	return row ? policyActionEffect(row, action) !== undefined : false;
}

/**
 * Find the agent row for an entity type or target agent. All null = blanket.
 */
function findAgentRow(
	agent: AgentPermissionPolicy[],
	resourceClass: MatrixResourceClass,
	entityTypeSlug: string | null,
	targetAgentId: string | null = null,
): AgentPermissionPolicy | undefined {
	return agent.find(
		(row) =>
			row.resource_class === resourceClass &&
			(row.entity_type_slug ?? null) === entityTypeSlug &&
			row.operation_key === null &&
			(row.target_agent_id ?? null) === targetAgentId,
	);
}

/**
 * The effects THIS scope's own agent row explicitly stores — the sparse override.
 */
export function storedEffectsForScope(
	response: AgentPermissionsResponse,
	resourceClass: MatrixResourceClass,
	entityTypeSlug: string | null,
	targetAgentId: string | null = null,
): Partial<Record<PermissionAction, PermissionEffect>> {
	const row = findAgentRow(
		response.agent,
		resourceClass,
		entityTypeSlug,
		targetAgentId,
	);
	const effects: Partial<Record<PermissionAction, PermissionEffect>> = {};
	if (!row?.effects) return effects;
	for (const action of CLASS_ACTIONS[resourceClass]) {
		const stored = row.effects[action];
		if (stored) effects[action] = stored;
	}
	return effects;
}

/**
 * Resolve the Effect for one action. Blanket + scoped exception fold
 * most-restrictively; floor is a hard lower bound only when a real floor row binds.
 */
export function resolveAttended(
	response: AgentPermissionsResponse,
	resourceClass: MatrixResourceClass,
	action: PermissionAction,
	entityTypeSlug: string | null,
	targetAgentId: string | null = null,
): PermissionEffect {
	const floor = resolveFloor(
		response.floor,
		resourceClass,
		action,
		entityTypeSlug,
		targetAgentId,
	);
	const floorBinds = floorRowNamesAction(
		response.floor,
		resourceClass,
		action,
		entityTypeSlug,
		targetAgentId,
	);
	const scopeRow =
		entityTypeSlug !== null || targetAgentId !== null
			? findAgentRow(
					response.agent,
					resourceClass,
					entityTypeSlug,
					targetAgentId,
				)
			: undefined;
	const blanketRow = findAgentRow(response.agent, resourceClass, null);
	let folded: PermissionEffect | null = null;
	for (const row of [blanketRow, scopeRow]) {
		const effect = row && policyActionEffect(row, action);
		if (effect)
			folded = folded === null ? effect : stricterEffect(folded, effect);
	}
	if (folded === null) return floor;
	return floorBinds ? stricterEffect(folded, floor) : folded;
}

/**
 * Whether a given effect option is selectable. Floor is the only hard bound
 * when a real org floor row binds.
 */
export function optionAvailability(args: {
	option: PermissionEffect;
	floor: PermissionEffect;
}): { disabled: boolean; reason?: string } {
	const { option, floor } = args;
	if (isLooserThan(option, floor)) {
		return {
			disabled: true,
			reason: `Below org floor (${EFFECT_LABEL[floor]})`,
		};
	}
	return { disabled: false };
}

/**
 * The set of entity types the matrix must render a per-type row for: any type that
 * carries an agent override (attended or autonomous) OR a type-scoped ORG FLOOR. A
 * type-specific floor is enforced by the backend even with no agent override, so
 * omitting it would show only the looser "All types" value while writes for that
 * type are actually denied/gated. Field- and single-entity scopes don't bind the
 * type matrix (same filter as {@link resolveFloor}) and are excluded.
 */
export function overriddenEntityTypes(
	response: AgentPermissionsResponse,
): AgentPermissionEntityType[] {
	const slugs = new Set<string>();
	for (const row of response.agent) {
		if (row.resource_class === "entity" && row.entity_type_slug) {
			slugs.add(row.entity_type_slug);
		}
	}
	for (const row of response.floor) {
		if (
			row.resource_class === "entity" &&
			row.entity_type_slug &&
			row.field_path === null &&
			row.entity_id === null
		) {
			slugs.add(row.entity_type_slug);
		}
	}
	const bySlug = new Map(response.entity_types.map((t) => [t.slug, t]));
	return [...slugs].map((slug) => bySlug.get(slug) ?? { slug, name: slug });
}

/**
 * Whether THIS AGENT has an actual per-type entity row for the slug (attended or
 * autonomous). A type may appear in {@link overriddenEntityTypes} solely because of
 * an org-FLOOR row; such a "floor-only" type has nothing agent-owned to remove, so
 * the matrix must NOT offer a Remove control (delete only targets exact-agent rows
 * and would no-op, then the refetch restores the row). Used to hide/disable Remove.
 */
export function agentOverridesType(
	response: AgentPermissionsResponse,
	entityTypeSlug: string,
): boolean {
	return response.agent.some(
		(row) =>
			row.resource_class === "entity" &&
			row.entity_type_slug === entityTypeSlug,
	);
}

/** Count how many actions of a per-type entity override differ from the default. */
export function countOverrideDiffs(
	response: AgentPermissionsResponse,
	entityTypeSlug: string,
): number {
	let diffs = 0;
	for (const action of CLASS_ACTIONS.entity) {
		const defaultEffect = resolveAttended(response, "entity", action, null);
		const typeEffect = resolveAttended(
			response,
			"entity",
			action,
			entityTypeSlug,
		);
		if (typeEffect !== defaultEffect) {
			diffs += 1;
		}
	}
	return diffs;
}

/** agent_config target-agent exceptions currently stored for this response. */
export function overriddenTargetAgents(
	response: AgentPermissionsResponse,
): AgentPermissionAgentRef[] {
	const ids = new Set<string>();
	for (const row of response.agent) {
		if (row.resource_class === "agent_config" && row.target_agent_id) {
			ids.add(row.target_agent_id);
		}
	}
	for (const row of response.floor) {
		if (row.resource_class === "agent_config" && row.target_agent_id) {
			ids.add(row.target_agent_id);
		}
	}
	const byId = new Map(response.agents.map((a) => [a.id, a]));
	return [...ids].map((id) => byId.get(id) ?? { id, name: id });
}

export function agentOverridesTarget(
	response: AgentPermissionsResponse,
	targetAgentId: string,
): boolean {
	return response.agent.some(
		(row) =>
			row.resource_class === "agent_config" &&
			row.target_agent_id === targetAgentId,
	);
}

/** Count agent_config actions that differ from the blanket for this target. */
export function countTargetAgentDiffs(
	response: AgentPermissionsResponse,
	targetAgentId: string,
): number {
	let diffs = 0;
	for (const action of CLASS_ACTIONS.agent_config) {
		const blank = resolveAttended(response, "agent_config", action, null);
		const scoped = resolveAttended(
			response,
			"agent_config",
			action,
			null,
			targetAgentId,
		);
		if (scoped !== blank) diffs += 1;
	}
	return diffs;
}
